import React from "react";
import {
  Users, Image as ImageIcon, MapPin, Box, Plus, Edit2, Trash2,
  RefreshCw, Upload, Check, X, Tag, Layers, Sparkles
} from "lucide-react";
import { Section, DemoButton, TipBox, DemoInput, DemoImageArea, StepIndicator } from "./HelpComponents";

const HelpAssets = ({ theme, expandedSection, toggleSection }) => {
  return (
    <div className="space-y-4">
      {/* 概述 */}
      <div className="p-4 rounded-xl border" style={{ backgroundColor: "var(--color-bg-secondary)", borderColor: "var(--color-border-primary)" }}>
        <p className="text-sm leading-relaxed" style={{ color: "var(--color-text-secondary)" }}>
          资产工坊用于管理项目中的<strong style={{ color: "var(--color-text-primary)" }}>角色、场景、道具</strong>。
          每个资产都有主图、描述、别名和变体。分镜生成时 AI 会自动匹配已有资产，保持画面一致性。
        </p>
        <div className="flex gap-3 mt-3">
          <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium" style={{ backgroundColor: `${theme.colors.accentPrimary}15`, color: theme.colors.accentPrimary }}>
            <Users size={14} /> 角色工坊
          </span>
          <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium" style={{ backgroundColor: `${theme.colors.info}15`, color: theme.colors.info }}>
            <MapPin size={14} /> 场景工坊
          </span>
          <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium" style={{ backgroundColor: `${theme.colors.warning}15`, color: theme.colors.warning }}>
            <Box size={14} /> 道具工坊
          </span>
        </div>
      </div>

      {/* 1. 工坊界面布局 */}
      <Section id={100} title="工坊界面布局" icon={Layers} expanded={expandedSection === 100} onToggle={toggleSection} theme={theme}>
        <div className="space-y-4">
          <p className="text-sm leading-relaxed" style={{ color: "var(--color-text-secondary)" }}>
            三个工坊的界面结构完全一致，以角色工坊为例：
          </p>
          {/* 模拟布局 */}
          <div className="rounded-xl border overflow-hidden" style={{ borderColor: "var(--color-border-primary)" }}>
            {/* 顶栏 */}
            <div className="flex items-center justify-between px-4 py-3 border-b" style={{ backgroundColor: "var(--color-bg-secondary)", borderColor: "var(--color-border-primary)" }}>
              <div className="flex items-center gap-2">
                <Users size={16} style={{ color: theme.colors.accentPrimary }} />
                <span className="text-sm font-bold" style={{ color: "var(--color-text-primary)" }}>角色工坊</span>
              </div>
              <DemoButton icon={Plus} label="新建角色" theme={theme} size="sm" />
            </div>
            {/* 内容区 */}
            <div className="flex" style={{ backgroundColor: "var(--color-bg-primary)" }}>
              {/* 左侧列表 */}
              <div className="w-40 border-r p-2 space-y-2 shrink-0" style={{ borderColor: "var(--color-border-primary)" }}>
                <div className="rounded-lg border-2 p-2" style={{ borderColor: theme.colors.accentPrimary, backgroundColor: "var(--color-bg-tertiary)" }}>
                  <div className="rounded bg-[var(--color-bg-tertiary)] mb-1.5" style={{ aspectRatio: "21/9" }}></div>
                  <div className="text-xs font-medium truncate" style={{ color: theme.colors.accentPrimary }}>林小凡</div>
                </div>
                <div className="rounded-lg border p-2" style={{ borderColor: "var(--color-border-primary)" }}>
                  <div className="rounded" style={{ aspectRatio: "21/9", backgroundColor: "var(--color-bg-overlay)" }}></div>
                  <div className="text-xs mt-1.5 truncate" style={{ color: "var(--color-text-muted)" }}>长老</div>
                </div>
                <div className="rounded-lg border p-2" style={{ borderColor: "var(--color-border-primary)" }}>
                  <div className="rounded" style={{ aspectRatio: "21/9", backgroundColor: "var(--color-bg-overlay)" }}></div>
                  <div className="text-xs mt-1.5 truncate" style={{ color: "var(--color-text-muted)" }}>师姐</div>
                </div>
              </div>
              {/* 右侧详情 */}
              <div className="flex-1 p-4 space-y-3">
                <div className="text-xs font-bold uppercase" style={{ color: "var(--color-text-muted)" }}>主体定妆照</div>
                <div className="rounded-lg border-2 border-dashed flex items-center justify-center" style={{ aspectRatio: "21/9", borderColor: "var(--color-border-primary)", backgroundColor: "var(--color-bg-overlay)" }}>
                  <div className="flex gap-2">
                    <DemoButton icon={Sparkles} label="AI 生成" theme={theme} size="sm" />
                    <DemoButton icon={Upload} label="上传图片" theme={theme} size="sm" variant="secondary" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div><div className="text-2xs font-bold mb-1" style={{ color: "var(--color-text-muted)" }}>名称</div><div className="text-xs" style={{ color: "var(--color-text-primary)" }}>林小凡</div></div>
                  <div><div className="text-2xs font-bold mb-1" style={{ color: "var(--color-text-muted)" }}>别名</div><div className="flex gap-1"><span className="px-1.5 py-0.5 rounded text-2xs" style={{ backgroundColor: "var(--color-bg-tertiary)", color: "var(--color-text-secondary)" }}>小凡</span><span className="px-1.5 py-0.5 rounded text-2xs" style={{ backgroundColor: "var(--color-bg-tertiary)", color: "var(--color-text-secondary)" }}>凡哥</span></div></div>
                </div>
              </div>
            </div>
          </div>
          <TipBox theme={theme}>左侧是资产列表，点击选中后右侧显示详情。点击右上角按钮可新建资产。</TipBox>
        </div>
      </Section>

      {/* 2. 新建资产 */}
      <Section id={101} title="新建资产" icon={Plus} expanded={expandedSection === 101} onToggle={toggleSection} theme={theme}>
        <div className="space-y-4">
          <p className="text-sm leading-relaxed" style={{ color: "var(--color-text-secondary)" }}>
            点击右上角的新建按钮，弹出创建对话框：
          </p>
          <div className="flex gap-3 mb-2">
            <DemoButton icon={Plus} label="新建角色" theme={theme} size="sm" />
            <DemoButton icon={Plus} label="新建场景" theme={theme} size="sm" />
            <DemoButton icon={Plus} label="新建道具" theme={theme} size="sm" />
          </div>
          {/* 模拟创建弹窗 */}
          <div className="p-4 rounded-xl border space-y-3" style={{ backgroundColor: "var(--color-bg-secondary)", borderColor: "var(--color-border-primary)" }}>
            <div className="text-sm font-bold" style={{ color: "var(--color-text-primary)" }}>创建新角色</div>
            <DemoInput label="画风" value="古风国漫" />
            <DemoInput label="角色名称" placeholder="例如：林小凡" />
            <DemoInput label="别名（逗号分隔）" placeholder="小凡, 凡哥, 他..." />
            <div>
              <label className="block text-xs font-bold uppercase mb-2" style={{ color: "var(--color-text-muted)" }}>描述</label>
              <div className="px-4 py-2.5 rounded-lg text-sm h-16" style={{ backgroundColor: "var(--color-bg-overlay)", border: "1px solid var(--color-border-primary)", color: "var(--color-text-muted)" }}>
                描述角色外貌、服装、发型...
              </div>
            </div>
            <DemoImageArea label="AI 生成 / 上传图片" ratio="21/9" theme={theme}>
              <div className="flex gap-2">
                <DemoButton icon={Sparkles} label="AI 生成" theme={theme} size="sm" />
                <DemoButton icon={Upload} label="上传图片" theme={theme} size="sm" variant="secondary" />
              </div>
            </DemoImageArea>
            <div className="flex justify-end">
              <DemoButton icon={Check} label="保存角色" theme={theme} variant="green" size="sm" />
            </div>
          </div>
          <div className="space-y-2">
            <StepIndicator number={1} label="填写名称和描述" theme={theme} />
            <StepIndicator number={2} label="选择 AI 生成或手动上传图片" theme={theme} />
            <StepIndicator number={3} label="点击保存" theme={theme} />
          </div>
        </div>
      </Section>

      {/* 3. 编辑资产 */}
      <Section id={102} title="编辑资产信息" icon={Edit2} expanded={expandedSection === 102} onToggle={toggleSection} theme={theme}>
        <div className="space-y-4">
          <p className="text-sm leading-relaxed" style={{ color: "var(--color-text-secondary)" }}>
            选中一个资产后，右侧面板显示详情。每个字段旁边都有编辑按钮：
          </p>
          {/* 模拟编辑区 */}
          <div className="space-y-3">
            {/* 主图操作 */}
            <div className="text-xs font-bold uppercase flex items-center justify-between" style={{ color: "var(--color-text-muted)" }}>
              <span>主体定妆照</span>
              <div className="flex gap-2">
                <DemoButton icon={Upload} label="替换" theme={theme} size="sm" variant="secondary" />
                <DemoButton icon={RefreshCw} label="重新生成" theme={theme} size="sm" variant="secondary" />
              </div>
            </div>
            <div className="rounded-lg bg-[var(--color-bg-tertiary)] border" style={{ aspectRatio: "21/9", borderColor: "var(--color-border-primary)" }}></div>

            {/* 名称 */}
            <div className="p-3 rounded-lg border" style={{ borderColor: "var(--color-border-primary)" }}>
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs font-bold uppercase" style={{ color: "var(--color-text-muted)" }}>角色名称</span>
              </div>
              <span className="text-sm" style={{ color: "var(--color-text-primary)" }}>林小凡</span>
            </div>

            {/* 别名 */}
            <div className="p-3 rounded-lg border" style={{ borderColor: "var(--color-border-primary)" }}>
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-bold uppercase flex items-center gap-1" style={{ color: "var(--color-text-muted)" }}>
                  <Tag size={12} /> 别名（智能匹配）
                </span>
                <DemoButton icon={Edit2} label="编辑" theme={theme} size="sm" variant="secondary" />
              </div>
              <div className="flex gap-1.5 flex-wrap">
                {["小凡", "凡哥", "林少侠"].map(a => (
                  <span key={a} className="px-2 py-1 rounded-md text-xs" style={{ backgroundColor: "var(--color-bg-tertiary)", color: "var(--color-text-secondary)" }}>{a}</span>
                ))}
              </div>
            </div>

            {/* 描述 */}
            <div className="p-3 rounded-lg border" style={{ borderColor: "var(--color-border-primary)" }}>
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-bold uppercase" style={{ color: "var(--color-text-muted)" }}>原始描述（AI 生成提示词）</span>
                <DemoButton icon={Edit2} label="编辑" theme={theme} size="sm" variant="secondary" />
              </div>
              <p className="text-xs leading-relaxed" style={{ color: "var(--color-text-secondary)" }}>
                18岁青衣少年，黑色长发束成马尾，剑眉星目，身穿青色道袍...
              </p>
            </div>
          </div>
          <TipBox theme={theme}>点击"编辑"按钮进入编辑模式，修改后点击 ✓ 保存或 ✕ 取消。</TipBox>
        </div>
      </Section>

      {/* 4. 别名系统 */}
      <Section id={103} title="别名与智能匹配" icon={Tag} expanded={expandedSection === 103} onToggle={toggleSection} theme={theme}>
        <div className="space-y-4">
          <p className="text-sm leading-relaxed" style={{ color: "var(--color-text-secondary)" }}>
            别名是资产的"昵称"列表。当 AI 分析剧本时，会用别名来匹配已有资产，避免重复创建。
          </p>
          {/* 示例 */}
          <div className="p-4 rounded-xl border" style={{ backgroundColor: "var(--color-bg-secondary)", borderColor: "var(--color-border-primary)" }}>
            <div className="text-xs font-bold mb-3" style={{ color: "var(--color-text-primary)" }}>示例：角色"秦婉儿"的别名</div>
            <div className="flex gap-2 flex-wrap mb-3">
              {["秦婉儿", "小秦", "婉儿", "阿婉", "她"].map(a => (
                <span key={a} className="px-2.5 py-1 rounded-lg text-xs font-medium" style={{ backgroundColor: `${theme.colors.accentPrimary}15`, color: theme.colors.accentPrimary }}>{a}</span>
              ))}
            </div>
            <div className="text-xs leading-relaxed" style={{ color: "var(--color-text-muted)" }}>
              当剧本中出现"小秦转身离去"时，AI 会自动匹配到"秦婉儿"这个角色资产。
            </div>
          </div>
          <div className="p-4 rounded-xl border" style={{ backgroundColor: "var(--color-bg-secondary)", borderColor: "var(--color-border-primary)" }}>
            <div className="text-xs font-bold mb-2" style={{ color: "var(--color-text-primary)" }}>编辑别名</div>
            <p className="text-xs mb-3" style={{ color: "var(--color-text-muted)" }}>点击"编辑"按钮后，输入框中用逗号分隔多个别名：</p>
            <div className="flex items-center gap-2">
              <div className="flex-1 px-3 py-2 rounded-lg text-xs" style={{ backgroundColor: "var(--color-bg-overlay)", border: "1px solid var(--color-border-primary)", color: "var(--color-text-primary)" }}>
                小秦, 婉儿, 阿婉, 她
              </div>
              <span className="p-1.5 rounded" style={{ backgroundColor: `${theme.colors.success}20`, color: theme.colors.success }}><Check size={14} /></span>
              <span className="p-1.5 rounded" style={{ backgroundColor: "var(--color-bg-tertiary)", color: "var(--color-text-muted)" }}><X size={14} /></span>
            </div>
          </div>
          <TipBox theme={theme} type="warning">别名越丰富，AI 匹配越准确。建议把剧本中可能出现的所有称呼都加上。</TipBox>
        </div>
      </Section>

      {/* 5. 变体系统 */}
      <Section id={104} title="变体系统（换装/换景）" icon={RefreshCw} expanded={expandedSection === 104} onToggle={toggleSection} theme={theme}>
        <div className="space-y-4">
          <p className="text-sm leading-relaxed" style={{ color: "var(--color-text-secondary)" }}>
            变体是同一资产的不同版本。比如同一个角色的"日常装"和"战斗装"，或同一个场景的"白天"和"夜晚"。
          </p>
          {/* 变体示例 */}
          <div className="p-4 rounded-xl border" style={{ backgroundColor: "var(--color-bg-secondary)", borderColor: "var(--color-border-primary)" }}>
            <div className="flex items-center justify-between mb-3">
              <span className="text-xs font-bold uppercase" style={{ color: "var(--color-text-muted)" }}>服装变体</span>
              <DemoButton icon={Plus} label="生成变体" theme={theme} size="sm" variant="green" />
            </div>
            <div className="grid grid-cols-3 gap-3">
              {["日常青衣", "战斗铠甲", "宴会礼服"].map((name, i) => (
                <div key={i} className="rounded-lg border overflow-hidden" style={{ borderColor: "var(--color-border-primary)" }}>
                  <div className="bg-[var(--color-bg-tertiary)]" style={{ aspectRatio: "21/9" }}></div>
                  <div className="px-2 py-1.5 text-xs font-medium truncate" style={{ color: "var(--color-text-primary)", backgroundColor: "var(--color-bg-secondary)" }}>{name}</div>
                </div>
              ))}
            </div>
          </div>

          {/* 生成变体流程 */}
          <div className="text-sm font-bold" style={{ color: "var(--color-text-primary)" }}>生成变体的两种方式</div>
          <div className="grid grid-cols-2 gap-3">
            <div className="p-3 rounded-xl border" style={{ backgroundColor: "var(--color-bg-secondary)", borderColor: "var(--color-border-primary)" }}>
              <div className="flex items-center gap-2 mb-2">
                <Edit2 size={14} style={{ color: theme.colors.accentPrimary }} />
                <span className="text-xs font-bold" style={{ color: "var(--color-text-primary)" }}>文字换装</span>
              </div>
              <p className="text-xs mb-2" style={{ color: "var(--color-text-muted)" }}>用文字描述新造型，AI 基于主图生成变体</p>
              <div className="px-3 py-2 rounded text-xs" style={{ backgroundColor: "var(--color-bg-overlay)", border: "1px solid var(--color-border-primary)", color: "var(--color-text-muted)" }}>
                例如：穿着黑色战斗铠甲，手持长剑
              </div>
            </div>
            <div className="p-3 rounded-xl border" style={{ backgroundColor: "var(--color-bg-secondary)", borderColor: "var(--color-border-primary)" }}>
              <div className="flex items-center gap-2 mb-2">
                <ImageIcon size={14} style={{ color: theme.colors.accentPrimary }} />
                <span className="text-xs font-bold" style={{ color: "var(--color-text-primary)" }}>参考图换装</span>
              </div>
              <p className="text-xs mb-2" style={{ color: "var(--color-text-muted)" }}>上传一张参考图，AI 将角色换成参考图的造型</p>
              <DemoImageArea label="点击上传参考图" ratio="21/9" theme={theme} />
            </div>
          </div>
          <TipBox theme={theme} type="success">变体创建后，在分镜编辑器中可以为每个镜头选择使用哪个变体，实现同一角色不同造型的切换。</TipBox>
        </div>
      </Section>

      {/* 6. 删除资产 */}
      <Section id={105} title="删除资产" icon={Trash2} expanded={expandedSection === 105} onToggle={toggleSection} theme={theme}>
        <div className="space-y-4">
          <p className="text-sm leading-relaxed" style={{ color: "var(--color-text-secondary)" }}>
            在左侧资产列表中，鼠标悬停在资产卡片上会出现删除按钮：
          </p>
          <div className="w-40">
            <div className="rounded-lg border p-2 relative" style={{ borderColor: "var(--color-border-primary)" }}>
              <div className="rounded bg-[var(--color-bg-tertiary)] mb-1.5" style={{ aspectRatio: "21/9" }}></div>
              <div className="text-xs truncate" style={{ color: "var(--color-text-primary)" }}>林小凡</div>
              <div className="absolute top-3 right-3 p-1 rounded" style={{ backgroundColor: "color-mix(in srgb, var(--color-error) 90%, transparent)", color: "white" }}>
                <Trash2 size={10} />
              </div>
            </div>
          </div>
          <TipBox theme={theme} type="warning">删除资产后，已引用该资产的分镜不会受影响，但无法再使用该资产生成新图片。</TipBox>
        </div>
      </Section>
    </div>
  );
};

export default HelpAssets;
